import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useAvailablePaymentMethods, usePromotePageState } from 'src/app/pages/promote/pageContext';
import type { IbanForm } from 'src/app/shared/components/payments';

import { useSharedHooks } from '../../../hooks/useSharedHooks';
import type { ExpandedPaymentMethod } from '../../shared/types';

type IbanFormProps = Parameters<typeof IbanForm>[0];

type IbanFormData = Pick<IbanFormProps, 'name' | 'email' | 'paymentMethodSaveOptions'>;

type IbanFormActions = Omit<IbanFormProps, keyof IbanFormData | 'submitButton'>;

type Result = ModelHookResult<
	IbanFormData & {
		displayed: boolean;
		expanded: boolean;
		ref: RefObject<HTMLSpanElement | null>;
		loading: boolean;
		disabled: boolean;
	},
	IbanFormActions & {
		handleExpandClick: () => void;
	}
>;

type Props = {
	collapsible: boolean;
	expandedPaymentMethod: ExpandedPaymentMethod | null;
	onToggle?: (paymentMethod: ExpandedPaymentMethod) => void;
};

export function useStripeSepa({ collapsible, expandedPaymentMethod, onToggle }: Props): Result {
	const [{ email, name, paymentMethodSaveOptions, formState }] = usePromotePageState();
	const { paymentMethods } = useAvailablePaymentMethods();
	const track = useTracking();
	const { inProgress, ...rest } = useSharedHooks();
	const ref = useRef<HTMLSpanElement>(null);

	const expanded = !collapsible || expandedPaymentMethod === 'sepa';

	useEffect(() => {
		if (expanded && collapsible) {
			ref.current?.focus();
		}
	}, [expanded, collapsible]);

	const onFieldFocus = useCallback(() => {
		void track('pp_focus_iban');
	}, [track]);

	const handleExpandClick = useCallback(() => {
		if (onToggle) {
			void track('pp_expand_sepa_form');
			onToggle('sepa');
		}
	}, [onToggle, track]);

	return {
		data: {
			displayed: paymentMethods.includes('sepa'),
			expanded,
			email,
			name,
			paymentMethodSaveOptions,
			loading: inProgress,
			disabled: inProgress || formState.status === 'loading',
			ref,
		},
		actions: {
			...rest,
			onFieldFocus,
			handleExpandClick,
		},
	};
}
